import React, { useState } from "react";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { vehicleService } from "../../services/api";
import { newRequestId } from "../../utils/requestId";

const input =
  "mt-1 block w-full rounded-md border border-[#dce3dd] bg-white p-2 text-sm";
const vehicleDefaults = {
  name: "",
  kind: "motorcycle",
  distance_unit: "km",
  notes: "",
  reading_every_days: 30,
  enabled: true,
};
const planDefaults = {
  title: "",
  interval_days: null,
  interval_distance: null,
  lead_days: 7,
  lead_distance: 0,
  instructions: "",
  enabled: true,
};
const statusLabels = {
  due: "Service due",
  approaching: "Coming up",
  needs_history: "History needed",
  monitoring: "Monitoring",
  paused: "Paused",
};
const number = (value) =>
  value === null || value === undefined
    ? "Not recorded"
    : value.toLocaleString();

function Editor({ editor, today, busy, save, close }) {
  const { kind, initial, vehicle } = editor;
  const [values, setValues] = useState(() => {
    const base =
      kind === "vehicle"
        ? vehicleDefaults
        : kind === "plan"
          ? planDefaults
          : kind === "reading"
            ? { observed_on: today, reading: null, notes: "" }
            : {
                occurred_on: today,
                reading: null,
                products: "",
                provider: "",
                notes: "",
                source_reference: "",
              };
    return Object.fromEntries(
      Object.entries(base).map(([key, value]) => [
        key,
        initial?.[key] ?? value,
      ]),
    );
  });
  const [requestId] = useState(newRequestId);
  const unit = vehicle?.distance_unit || values.distance_unit;
  const set = (key, value) => setValues((old) => ({ ...old, [key]: value }));
  const field = (key, title, type = "text", extra = {}) => (
    <label className="block text-xs font-semibold text-[#58635d]" key={key}>
      {title}
      {type === "textarea" ? (
        <textarea
          className={input}
          rows={3}
          maxLength={5000}
          value={values[key]}
          onChange={(e) => set(key, e.target.value)}
          {...extra}
        />
      ) : type === "select" ? (
        <select
          className={input}
          value={values[key]}
          onChange={(e) => set(key, e.target.value)}
        >
          {extra.options.map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      ) : (
        <input
          className={input}
          type={type}
          value={values[key] ?? ""}
          onInput={
            type === "date" ? (e) => set(key, e.target.value) : undefined
          }
          onChange={(e) =>
            set(
              key,
              type === "number"
                ? e.target.value === ""
                  ? null
                  : Number(e.target.value)
                : e.target.value,
            )
          }
          {...extra}
        />
      )}
    </label>
  );
  const title =
    kind === "vehicle"
      ? initial
        ? "Edit vehicle"
        : "Add vehicle"
      : kind === "plan"
        ? initial
          ? "Edit service schedule"
          : "Add service schedule"
        : kind === "reading"
          ? "Record odometer"
          : "Log completed service";
  return (
    <form
      className="surface-panel space-y-4 p-5"
      aria-label={title}
      onSubmit={(event) => {
        event.preventDefault();
        save({
          ...values,
          ...(!initial ? { request_id: requestId } : {}),
          ...(kind === "reading" || (kind === "plan" && !initial)
            ? { vehicle_id: vehicle.id }
            : {}),
          ...(kind === "service" ? { plan_id: editor.plan.id } : {}),
        });
      }}
    >
      <h3 className="font-bold">
        {title}
        {vehicle ? ` · ${vehicle.name}` : ""}
        {editor.plan ? ` · ${editor.plan.title}` : ""}
      </h3>
      {kind === "vehicle" && (
        <>
          {field("name", "Vehicle name", "text", {
            required: true,
            maxLength: 180,
            autoFocus: true,
          })}
          <div className="grid gap-3 sm:grid-cols-2">
            {field("kind", "Vehicle type", "select", {
              options: [
                ["motorcycle", "Motorcycle"],
                ["car", "Car"],
                ["bicycle", "Bicycle"],
                ["other", "Other"],
              ],
            })}
            {field("distance_unit", "Odometer units", "select", {
              options: [
                ["km", "Kilometres (km)"],
                ["mi", "Miles (mi)"],
              ],
            })}
            {field(
              "reading_every_days",
              "Request a fresh reading every (days)",
              "number",
              { min: 1, max: 365, required: true },
            )}
          </div>
          {field("notes", "Vehicle notes", "textarea")}
          <p className="text-xs text-[#68746d]">
            Units are fixed once a schedule or reading exists. Reading reminders
            apply when a distance-based schedule is enabled.
          </p>
        </>
      )}
      {kind === "plan" && (
        <>
          {field("title", "Service name", "text", {
            required: true,
            maxLength: 180,
            autoFocus: true,
          })}
          <p className="text-sm text-[#68746d]">
            Enter the interval from your service manual or agreed maintenance
            schedule. When both are set, the first threshold reached triggers
            attention.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            {field(
              "interval_days",
              "Service every (days, optional)",
              "number",
              { min: 1, max: 3650 },
            )}
            {field(
              "interval_distance",
              `Service every (${unit}, optional)`,
              "number",
              { min: 1, max: 1000000 },
            )}
            {field("lead_days", "Time reminder ahead (days)", "number", {
              min: 0,
              max: 90,
              required: true,
            })}
            {field(
              "lead_distance",
              `Distance reminder ahead (${unit})`,
              "number",
              { min: 0, max: 100000, required: true },
            )}
          </div>
          {field(
            "instructions",
            "Schedule source / maintenance instructions",
            "textarea",
          )}
          <p className="text-xs text-[#68746d]">
            Add at least one interval, then log the last completed service. A
            reminder does not count as completed work.
          </p>
        </>
      )}
      {(kind === "reading" || kind === "service") && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            {field(
              kind === "reading" ? "observed_on" : "occurred_on",
              kind === "reading" ? "Reading date" : "Completed service date",
              "date",
              { min: "1900-01-01", max: today, required: true },
            )}
            {field(
              "reading",
              `Odometer (${unit}${kind === "service" ? ", optional if unknown" : ""})`,
              "number",
              { min: 0, max: 10000000, step: 1, required: kind === "reading" },
            )}
          </div>
          {kind === "service" && (
            <>
              {field("products", "Products / specification used", "textarea", {
                maxLength: 2000,
              })}
              {field("provider", "Workshop / service provider", "text", {
                maxLength: 240,
              })}
              {field(
                "source_reference",
                "Receipt or source note reference",
                "text",
                { maxLength: 1000 },
              )}
              <p className="text-xs text-[#68746d]">
                Record work that actually happened. If its reading is unknown,
                leave it blank; the distance threshold will remain unknown.
              </p>
            </>
          )}
          {field("notes", "Record notes", "textarea")}
          <p className="text-xs text-[#68746d]">
            History can be entered in any order. Conflicting readings need a
            correction; Timelyzer does not estimate mileage.
          </p>
        </>
      )}
      {(kind === "vehicle" || kind === "plan") && (
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={values.enabled}
            onChange={(e) => set("enabled", e.target.checked)}
          />
          {kind === "vehicle"
            ? "Vehicle monitoring enabled"
            : "Service schedule enabled"}
        </label>
      )}
      <div className="flex gap-2">
        <button className="primary-button" disabled={busy}>
          {kind === "vehicle"
            ? "Save vehicle"
            : kind === "plan"
              ? "Save schedule"
              : kind === "reading"
                ? "Save reading"
                : "Save completed service"}
        </button>
        <button
          type="button"
          className="secondary-button"
          disabled={busy}
          onClick={close}
        >
          Cancel
        </button>
      </div>
    </form>
  );
}

function History({ vehicle, plans, busy, run, close }) {
  const [kind, setKind] = useState("services");
  const [correction, setCorrection] = useState(null);
  const [reason, setReason] = useState("");
  const query = useInfiniteQuery({
    queryKey: ["vehicle-history", vehicle.id, kind],
    queryFn: ({ pageParam }) =>
      vehicleService.history(vehicle.id, kind, pageParam),
    initialPageParam: undefined,
    getNextPageParam: (page) => page.next_cursor || undefined,
  });
  return (
    <section
      className="surface-panel space-y-4 p-5"
      aria-label={`History for ${vehicle.name}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-bold">History · {vehicle.name}</h3>
        <button className="quiet-button" onClick={close}>
          Close history
        </button>
      </div>
      <div className="flex gap-2">
        {[
          ["services", "Completed services"],
          ["readings", "Odometer history"],
        ].map(([value, label]) => (
          <button
            key={value}
            aria-pressed={kind === value}
            className={kind === value ? "primary-button" : "secondary-button"}
            onClick={() => {
              setKind(value);
              setCorrection(null);
            }}
          >
            {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-[#68746d]">
        Newest entries first. Voided records stay visible but no longer affect
        reminders or readings.
      </p>
      {query.isLoading && <p role="status">Loading history…</p>}
      {query.error && (
        <p role="alert">
          Could not load history.{" "}
          <button className="quiet-button" onClick={() => query.refetch()}>
            Retry history
          </button>
        </p>
      )}
      {query.data?.pages
        .flatMap((page) => page.items)
        .map((item) => (
          <article
            className="rounded-lg border border-[#e1e7e1] p-4"
            key={item.id}
          >
            <p className="text-sm font-semibold">
              {kind === "services"
                ? plans.find((plan) => plan.id === item.plan_id)?.title ||
                  "Recorded service"
                : "Odometer reading"}{" "}
              · {item.occurred_on || item.observed_on}
              {item.voided ? " · Voided" : ""}
            </p>
            <p className="mt-1 text-sm">
              {number(item.reading)}
              {item.reading !== null ? ` ${vehicle.distance_unit}` : ""}
              {item.provider ? ` · ${item.provider}` : ""}
            </p>
            {item.products && (
              <p className="mt-2 whitespace-pre-wrap text-sm">
                {item.products}
              </p>
            )}
            {item.notes && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-[#68746d]">
                {item.notes}
              </p>
            )}
            {item.source_reference && (
              <p className="mt-2 break-words text-xs">
                Source: {item.source_reference}
              </p>
            )}
            {item.voided ? (
              <p className="mt-2 text-xs">
                Correction reason: {item.void_reason}
              </p>
            ) : item.service_id ? (
              <p className="mt-2 text-xs">
                Recorded with a service. Correct it in Completed services.
              </p>
            ) : (
              <button
                className="quiet-button mt-2 text-xs"
                onClick={() => {
                  setCorrection(item);
                  setReason("");
                }}
              >
                Correct this entry
              </button>
            )}
          </article>
        ))}
      {query.data && !query.data.pages[0].items.length && (
        <p className="text-sm text-[#68746d]">No history recorded yet.</p>
      )}
      {query.hasNextPage && (
        <button
          className="secondary-button"
          disabled={query.isFetchingNextPage}
          onClick={() => query.fetchNextPage()}
        >
          Load older entries
        </button>
      )}
      {correction && (
        <form
          className="rounded-lg border border-[#e6dfcb] bg-[#fffdf4] p-4 space-y-3"
          aria-label="Correct history entry"
          onSubmit={(e) => {
            e.preventDefault();
            run(async () => {
              await vehicleService.void(kind, correction.id, reason);
              setCorrection(null);
            });
          }}
        >
          <h4 className="font-semibold">Void incorrect entry</h4>
          <p className="text-sm">
            {correction.occurred_on || correction.observed_on} ·{" "}
            {number(correction.reading)} {vehicle.distance_unit}. This excludes
            the entry
            {kind === "services" ? " and its linked odometer reading" : ""} from
            calculations. Afterwards, record the corrected facts as a new entry.
          </p>
          <label className="block text-xs font-semibold">
            Reason for correction
            <input
              className={input}
              required
              minLength={3}
              maxLength={1000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <div className="flex gap-2">
            <button className="primary-button" disabled={busy}>
              Void this entry
            </button>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setCorrection(null)}
            >
              Keep entry
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

export default function VehicleDesk({ run, busy }) {
  const query = useQuery({
    queryKey: ["vehicles"],
    queryFn: vehicleService.overview,
    refetchInterval: 60000,
  });
  const [editor, setEditor] = useState(null);
  const [historyId, setHistoryId] = useState(null);
  if (query.isLoading) return <p role="status">Loading vehicles…</p>;
  if (query.error)
    return (
      <p role="alert">
        Could not load vehicles.{" "}
        <button className="secondary-button" onClick={() => query.refetch()}>
          Retry vehicles
        </button>
      </p>
    );
  const data = query.data;
  const historyVehicle = data.vehicles.find(
    (vehicle) => vehicle.id === historyId,
  );
  const save = (values) =>
    run(async () => {
      if (editor.kind === "vehicle")
        await (editor.initial
          ? vehicleService.edit(editor.initial.id, values)
          : vehicleService.create(values));
      else if (editor.kind === "plan")
        await (editor.initial
          ? vehicleService.editPlan(editor.initial.id, values)
          : vehicleService.createPlan(values));
      else if (editor.kind === "reading")
        await vehicleService.recordReading(values);
      else await vehicleService.recordService(values);
      setEditor(null);
    });
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold">Vehicles & maintenance</h2>
          <p className="mt-1 text-sm text-[#68746d]">
            Service history, dated odometer readings, and the next maintenance
            step.
          </p>
        </div>
        <button
          className="primary-button"
          onClick={() => setEditor({ kind: "vehicle" })}
        >
          Add vehicle
        </button>
      </div>
      {editor && (
        <Editor
          key={`${editor.kind}:${editor.initial?.id || editor.vehicle?.id || "new"}:${editor.plan?.id || ""}`}
          editor={editor}
          today={data.today}
          busy={busy}
          save={save}
          close={() => setEditor(null)}
        />
      )}
      {historyVehicle && (
        <History
          key={historyVehicle.id}
          vehicle={historyVehicle}
          plans={data.plans}
          busy={busy}
          run={run}
          close={() => setHistoryId(null)}
        />
      )}
      {!data.vehicles.length && (
        <div className="surface-panel p-6 text-sm text-[#68746d]">
          Add a vehicle and its service schedule, then record the last completed
          work. Timelyzer checks time and distance thresholds and asks for fresh
          readings when needed.
        </div>
      )}
      {data.vehicles.map((vehicle) => (
        <section
          className="surface-panel p-5"
          key={vehicle.id}
          aria-label={vehicle.name}
        >
          <div className="flex flex-wrap justify-between gap-3">
            <div>
              <h3 className="font-bold">
                {vehicle.name}
                {!vehicle.enabled ? " · Paused" : ""}
              </h3>
              <p className="mt-1 text-sm text-[#68746d]">
                {vehicle.current_reading
                  ? `${number(vehicle.current_reading.reading)} ${vehicle.distance_unit} recorded ${vehicle.current_reading.observed_on}`
                  : "No odometer reading recorded"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                className="quiet-button text-xs"
                onClick={() => setEditor({ kind: "vehicle", initial: vehicle })}
              >
                Edit vehicle
              </button>
              <button
                className="secondary-button text-xs"
                onClick={() => setHistoryId(vehicle.id)}
              >
                History
              </button>
            </div>
          </div>
          {vehicle.notes && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-[#68746d]">
              {vehicle.notes}
            </p>
          )}
          {vehicle.enabled && (
            <div className="my-4 flex flex-wrap gap-2">
              <button
                className="secondary-button text-xs"
                onClick={() => setEditor({ kind: "reading", vehicle })}
              >
                Record odometer
              </button>
              <button
                className="secondary-button text-xs"
                onClick={() => setEditor({ kind: "plan", vehicle })}
              >
                Add service schedule
              </button>
            </div>
          )}
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            {data.plans
              .filter((plan) => plan.vehicle_id === vehicle.id)
              .map((plan) => (
                <article
                  className="rounded-lg border border-[#e1e7e1] p-4"
                  key={plan.id}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="font-semibold">{plan.title}</h4>
                    <span
                      className={`status-pill ${["due", "needs_history"].includes(plan.status) ? "status-pill-warning" : "status-pill-info"}`}
                    >
                      {statusLabels[plan.status]}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-[#68746d]">
                    Every{" "}
                    {[
                      plan.interval_days ? `${plan.interval_days} days` : null,
                      plan.interval_distance
                        ? `${number(plan.interval_distance)} ${vehicle.distance_unit}`
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" or ")}
                    {plan.interval_days && plan.interval_distance
                      ? ", whichever comes first"
                      : ""}
                    .
                  </p>
                  <dl className="mt-3 space-y-1 text-sm">
                    <div>
                      <dt className="inline text-[#68746d]">
                        Last completed:{" "}
                      </dt>
                      <dd className="inline">
                        {plan.last_service
                          ? `${plan.last_service.occurred_on} · ${number(plan.last_service.reading)}${plan.last_service.reading !== null ? ` ${vehicle.distance_unit}` : ""}`
                          : "Not recorded"}
                      </dd>
                    </div>
                    {plan.interval_days && (
                      <div>
                        <dt className="inline text-[#68746d]">Next date: </dt>
                        <dd className="inline">
                          {plan.due_on || "Needs service history"}
                        </dd>
                      </div>
                    )}
                    {plan.interval_distance && (
                      <div>
                        <dt className="inline text-[#68746d]">
                          Next odometer:{" "}
                        </dt>
                        <dd className="inline">
                          {plan.due_reading === null
                            ? "Needs service reading"
                            : `${number(plan.due_reading)} ${vehicle.distance_unit}`}
                          {plan.remaining_distance !== null
                            ? ` (${number(Math.abs(plan.remaining_distance))} ${vehicle.distance_unit} ${plan.remaining_distance < 0 ? "past threshold" : "remaining"})`
                            : ""}
                        </dd>
                      </div>
                    )}
                  </dl>
                  {plan.reason && (
                    <p className="mt-3 text-xs text-[#996617]">{plan.reason}</p>
                  )}
                  {plan.instructions && (
                    <p className="mt-2 whitespace-pre-wrap text-xs text-[#68746d]">
                      {plan.instructions}
                    </p>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {vehicle.enabled && plan.enabled && (
                      <button
                        className="primary-button text-xs"
                        onClick={() =>
                          setEditor({ kind: "service", vehicle, plan })
                        }
                      >
                        Log completed service
                      </button>
                    )}
                    <button
                      className="quiet-button text-xs"
                      onClick={() =>
                        setEditor({ kind: "plan", initial: plan, vehicle })
                      }
                    >
                      Edit schedule
                    </button>
                  </div>
                </article>
              ))}
          </div>
          {!data.plans.some((plan) => plan.vehicle_id === vehicle.id) && (
            <p className="mt-3 text-sm text-[#68746d]">
              No maintenance schedule recorded.
            </p>
          )}
        </section>
      ))}
      <p className="text-xs text-[#68746d]">
        Reminders appear in Attention. Enable background monitoring for checks
        while you are away. Service dates use {data.timezone} at 9am; mileage
        checks use actual recorded readings.
      </p>
    </div>
  );
}
